import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaHome, FaChevronRight } from 'react-icons/fa';
import './PageHeader.css';

interface Breadcrumb {
  label: string;
  link?: string;
}

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  background?: string;
  breadcrumbs?: Breadcrumb[];
  /**
   * Set to 'p' on pages that render their own <h1> further down (the product
   * page does). The banner still shows the title and still feeds the trailing
   * breadcrumb — it just stops competing for the single h1.
   */
  titleAs?: 'h1' | 'p';
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  background = '/images/background/header-bg.webp',
  breadcrumbs = [],
  titleAs = 'h1'
}) => {
  const { t } = useTranslation();
  const Title = motion[titleAs];

  return (
    <section 
      className="header-section"
      style={{ backgroundImage: `url(${background})` }}
    >
      <div className="header-overlay"></div>
      <div className="container">
        <div className="header-content">
          <Title
            className="header-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {title}
          </Title>

          {subtitle && (
            <motion.p
              className="header-subtitle"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {subtitle}
            </motion.p>
          )}

          <motion.div
            className="breadcrumbs"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="breadcrumb-item">
              <Link to="/" className="breadcrumb-link">
                <FaHome />
                <span>{t('nav.home')}</span>
              </Link>
            </div>

            {breadcrumbs.map((crumb, index) => (
              <div className="breadcrumb-item" key={index}>
                <FaChevronRight />
                {crumb.link ? (
                  <Link to={crumb.link} className="breadcrumb-link">
                    {crumb.label}
                  </Link>
                ) : (
                  <span>{crumb.label}</span>
                )}
              </div>
            ))}

            <div className="breadcrumb-item">
              <FaChevronRight />
              <span>{title}</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PageHeader;
